import React, { useEffect, useState, useMemo } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { stripePromise } from "../lib/stripe";
import Button from "../ui/Button.jsx";

const DEFAULT_RETURN_PATH = "/discover/marketplace";

function PayForm({ onClose, returnTo }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
  }, [elements]);

  const returnPath = returnTo || DEFAULT_RETURN_PATH;
  const buildReturnUrl = (extraParams = {}) => {
    const url = new URL(`${window.location.origin}/payment-success`);
    url.searchParams.set("returnTo", returnPath);
    Object.entries(extraParams || {}).forEach(([key, value]) => {
      if (value == null) return;
      url.searchParams.set(key, value);
    });
    return url.toString();
  };

  const handleConfirm = async () => {
    setError("");
    if (!stripe || !elements) {
      setError("Stripe has not loaded yet. Please wait a moment.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: buildReturnUrl(),
        },
        redirect: "if_required",
      });

      if (result.error) {
        setError(result.error.message || "Payment confirmation failed.");
        return;
      }

      const status = result.paymentIntent?.status;
      const piId = result.paymentIntent?.id;

      if (status === "succeeded" || status === "processing") {
        onClose?.();
        window.location.assign(
          buildReturnUrl(piId ? { payment_intent: piId } : {})
        );
        return;
      }

      setError(`Unexpected payment status: ${status || "unknown"}.`);
    } catch (err) {
      setError(err?.message || "Payment failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="p-3 rounded-md border border-white/10 bg-white/5">
        <PaymentElement options={{ layout: "tabs" }} />
      </div>
      {error && <div className="text-red-300 text-sm">{error}</div>}
      <div className="flex justify-end gap-2">
        <Button variant="soft" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={submitting}>
          {submitting ? "Confirming…" : "Confirm payment"}
        </Button>
      </div>
    </div>
  );
}

export default function ShopPayNowModal({ open, clientSecret, onClose, returnTo }) {
  if (!open) return null;
  if (!clientSecret) return null;

  // Memoize Elements options to prevent unnecessary remounts of Elements component
  // which can cause Stripe frames to reinitialize and trigger message port errors.
  const elementsOptions = useMemo(
    () => ({
      clientSecret,
      appearance: { theme: "stripe" },
    }),
    [clientSecret]
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3200,
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "min(480px, 100%)",
          background: "#0f172a",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 12,
          padding: 20,
          color: "#fff",
          boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3">
          <div className="text-lg font-semibold">Complete payment</div>
      <div className="text-sm opacity-70">
        Enter your payment details to confirm. Payment is processed automatically.
      </div>
        </div>
          <Elements stripe={stripePromise} options={elementsOptions}>
            <PayForm onClose={onClose} returnTo={returnTo} />
          </Elements>
      </div>
    </div>
  );
}
